/**
 * Reports module — shared shapes used across the report UI.
 *
 * Most types re-export from the YC demo contracts (`@/modules/shared/types/yc-demo`).
 * The Cv* / AI* shapes mirror the backend Mongoose docs in:
 *   - apps/server/modules/cv-screening/models/CvScreeningResult.model.js
 *   - apps/server/modules/ai-interview/models/AiInterviewAttemptSnapshot.model.js
 */

import type {
    JacintaMemoryEntry,
    MemoryAppliesTo,
    ReviewerScoreEdit,
    TaskItem,
} from '@/modules/shared/types/yc-demo';

export type { JacintaMemoryEntry, MemoryAppliesTo, ReviewerScoreEdit, TaskItem };

export type ReportKind = 'cv_screening' | 'ai_interview';

// ───────── CV screening result (mirrors backend) ─────────

export interface OverrideRow {
    reviewerId: string;
    reviewerName?: string;
    viaInvite?: boolean;
    score?: number | null;
    verdict?: boolean | null;
    reasoning?: string;
    submittedAt?: string;
    editHistory?: Array<{ score?: number | null; verdict?: boolean | null; reasoning?: string; at?: string }>;
}

export interface CvCriterionItem {
    criterionId: string;
    label: string;
    /** Legacy AI-original verdict; present on every doc post-migration. */
    isFulfilled: boolean;
    /** AI-original (immutable). Same as `isFulfilled` post-migration. */
    aiVerdict?: boolean | null;
    /** Effective verdict after multi-reviewer aggregation (majority vote, AI tiebreak). */
    effectiveVerdict?: boolean | null;
    overrides?: OverrideRow[];
    justification?: string;
}

export interface CvCriteriaResult {
    criteriaTotal: number;
    criteriaMet: number;
    criteria: CvCriterionItem[];
}

export interface CvStrengthComponent {
    componentId: string;
    label: string;
    score: number;
    scoreMax: number;
    aiScore?: number | null;
    effectiveScore?: number | null;
    overrides?: OverrideRow[];
    justification?: string;
}

export interface CvProfileStrength {
    totalScoreMax: number;
    totalScore: number;
    /** Sum of effective scores across components (post-override). */
    effectiveTotalScore?: number | null;
    components: CvStrengthComponent[];
}

export interface CvScreeningResult {
    _id: string;
    applicationId: string;
    jobPostingId: string;
    organizationId: string;
    status: 'pending' | 'screening' | 'completed' | 'error';
    criteriaResult: CvCriteriaResult | null;
    profileStrength: CvProfileStrength | null;
    /** AI-original verdict (e.g. 'STRONG_FIT', 'PASS'). */
    verdict: string | null;
    /** Effective verdict after overrides + requirement-fail veto. */
    effectiveVerdict?: string | null;
    /** Effective overall score (0-100). Profile strength %; requirement fail forces null/FAIL. */
    effectiveScore?: number | null;
    reviewerFeedback: ReviewerScoreEdit[];
    createdAt: string;
    updatedAt: string;
}

// ───────── AI interview snapshot (mirrors backend) ─────────

export interface AiPerRequirement {
    requirementId: string;
    label: string;
    isFulfilled: boolean;
    aiVerdict?: boolean | null;
    effectiveVerdict?: boolean | null;
    overrides?: OverrideRow[];
    justification?: string;
}

export interface AiPerCriterion {
    criterionId: string;
    label: string;
    score: number;
    scoreMax: number;
    aiScore?: number | null;
    effectiveScore?: number | null;
    overrides?: OverrideRow[];
    justification?: string;
}

export interface AiInterviewResult {
    _id: string;
    applicationId: string;
    jobPostingId: string;
    organizationId: string;
    status: 'pending' | 'invited' | 'analyzing' | 'completed' | 'error';
    perRequirement: AiPerRequirement[];
    perCriterion: AiPerCriterion[];
    overallScore: number | null;
    /** Effective overall score after multi-reviewer aggregation. */
    effectiveScore?: number | null;
    /** AI-original verdict. */
    verdict: string | null;
    /** Effective verdict after override + requirement-fail veto. */
    effectiveVerdict?: string | null;
    transcript: string | null;
    transcriptRef: string | null;
    recordingKey: string | null;
    reviewerFeedback: ReviewerScoreEdit[];
    completedAt?: string;
    createdAt: string;
    updatedAt: string;
}

// ───────── Helpers ─────────

/**
 * Latest reviewer edit per `componentId` (latest entry wins). Returns the
 * effective score the UI should render, or `undefined` if no edits exist.
 */
export function effectiveScoreFor(
    feedback: ReviewerScoreEdit[],
    componentId: string,
): ReviewerScoreEdit | undefined {
    let latest: ReviewerScoreEdit | undefined;
    for (const edit of feedback) {
        if (edit.componentId === componentId) {
            if (!latest || new Date(edit.editedAt) >= new Date(latest.editedAt)) {
                latest = edit;
            }
        }
    }
    return latest;
}

export function appliesToFor(kind: ReportKind): MemoryAppliesTo[] {
    return [kind];
}

// ───────── Universal Evaluation Framework Aggregate ─────────

export type EvaluatedAt = 'cv_screening' | 'ai_interview';

export interface FrameworkRequirementDef {
    _id: string;
    label: string;
    description?: string;
    evaluatedAt?: EvaluatedAt[];
}

export interface FrameworkScoreLevel {
    score: number;
    name: string;
    description?: string;
}

export interface FrameworkCriterionDef {
    _id: string;
    title?: string;
    label: string;
    description?: string;
    weight?: number;
    evaluatedAt?: EvaluatedAt[];
    levels?: FrameworkScoreLevel[];
}

export interface AggregatedRequirement {
    requirementId: string;
    label: string;
    description?: string;
    evaluatedAt: EvaluatedAt[];
    cvVerdict: boolean | null;
    aiVerdict: boolean | null;
    /** AND of cvVerdict and aiVerdict when both present, else single source. */
    effectiveVerdict: boolean | null;
}

export interface AggregatedCriterion {
    criterionId: string;
    title?: string;
    label: string;
    description?: string;
    weight: number;
    scoreMax: number;
    evaluatedAt: EvaluatedAt[];
    levels?: FrameworkScoreLevel[];
    cvScore: number | null;
    aiScore: number | null;
    /** Mean of cv+ai when both present, else single source, else null. */
    effectiveScore: number | null;
    sources: EvaluatedAt[];
}

export interface UniversalAggregate {
    requirements: AggregatedRequirement[];
    criteria: AggregatedCriterion[];
    requirementsMet: number;
    requirementsTotal: number;
    anyRequirementFailed: boolean;
    weightedSum: number;
    totalWeight: number;
    evaluatedCount: number;
    /** 0-100 weighted average across criteria with at least one source. */
    score: number | null;
    verdict: 'PASS' | 'REVIEW' | 'FAIL' | 'PENDING';
}

export interface UniversalAggregateResponse {
    framework: {
        requirements: FrameworkRequirementDef[];
        criteria: FrameworkCriterionDef[];
    };
    aggregate: UniversalAggregate;
    sources: {
        cv: { present: boolean; resultId: string | null; completedAt: string | null };
        ai: { present: boolean; resultId: string | null; completedAt: string | null };
    };
    jobTitle: string | null;
}
